/**
 * Copyright (c) 2018-2024 itemis AG - All rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Contributors:
 * 	andreas muelder - itemis AG
 *
 */
import { dia, shapes, util } from "@clientio/rappid";
import { PreferenceStore } from "../services/preference-store";
import { LabeledNode, LabeledNodeController, LabeledNodeView } from "./base/labeled-node";
import { removeStaticAttributes } from "./base/utils";

export const LABEL_PADDING = 5;

const markups: { [key: string]: dia.MarkupJSON } = {
  Initial: util.svg`
    <circle @selector="circle"/>
  `,
  DeepHistory: util.svg`
      <circle @selector="circle" />
      <path @selector="history" />
      <path @selector="star" />
  `,
  ShallowHistory: util.svg`
     <circle @selector="circle" />
     <path @selector="history" />
  `,
};

export class Entry extends LabeledNode {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(...args: any[]) {
    super(...args);
    this.on("change:entryKind", () => {
      this.updateMarkup();
    });
    this.updateMarkup();
  }

  updateMarkup(): void {
    this.set("markup", markups[this.getEntryKind()] || markups["Initial"]);
  }

  defaults() {
    return {
      ...super.defaults,
      type: "Entry",
      entryKind: "Initial",
      size: {
        height: 18,
        width: 18,
      },
      fixedRatio: true,
      embedable: false,
      linkable: true,
      attrs: {
        root: {
          magnetSelector: "circle",
        },
        circle: {
          cx: "calc(0.5*w)",
          cy: "calc(0.5*h)",
          r: "calc(0.5*w)",
        },
        history: {
          d: `M calc(0.25 * w),calc(0.2 * w) 
              L calc(0.35 * w),calc(0.2 * w) 
              L calc(0.35 * w),calc(0.45 * w) 
              L calc(0.65 * w),calc(0.45 * w) 
              L calc(0.65 * w),calc(0.55 * w) 
              L calc(0.35 * w),calc(0.55 * w) 
              L calc(0.35 * w),calc(0.8 * w) 
              L calc(0.25 * w),calc(0.8 * w) 
              Z 
              M calc(0.65 * w),calc(0.2 * w) 
              L calc(0.75 * w),calc(0.2 * w) 
              L calc(0.75 * w),calc(0.8 * w) 
              L calc(0.65 * w),calc(0.8 * w) 
              Z`,
        },
        star: {
          d: `M calc(0.75 * w),calc(0.02 * w) 
              L calc(0.8 * w),calc(0.14 * w) 
              L calc(0.95 * w),calc(0.14 * w) 
              L calc(0.83 * w),calc(0.24 * w) 
              L calc(0.88 * w),calc(0.38 * w) 
              L calc(0.75 * w),calc(0.28 * w) 
              L calc(0.62 * w),calc(0.38 * w) 
              L calc(0.67 * w),calc(0.24 * w) 
              L calc(0.55 * w),calc(0.14 * w) 
              L calc(0.7 * w),calc(0.14 * w) 
              Z`,
          strokeWidth: 0.5,
        },
      },
    };
  }

  getEntryKind(): string {
    return this.get("entryKind");
  }

  setEntryKind(entryKind: "Initial" | "DeepHistory" | "ShallowHistory") {
    return this.set("entryKind", entryKind);
  }

  getFillColor(): string {
    return this.attr(["circle", "fill"]);
  }

  setFillColor(color: string | null) {
    this.attr(["circle", "fill"], color);
    if (color === null) {
      this.removeAttr(["circle", "fill"]);
    }
  }

  getBorderColor(): string {
    return this.attr(["circle", "stroke"]);
  }

  setBorderColor(color: string | null) {
    this.attr(["circle", "stroke"], color);
    if (color === null) {
      this.removeAttr(["circle", "stroke"]);
    }
  }

  getBorderWidth(): number {
    return this.attr(["circle", "strokeWidth"]) || 0;
  }

  setBorderWidth(width: number) {
    this.attr(["circle", "strokeWidth"], width);
  }

  resetDefaults() {
    this.setFillColor(null);
    this.setBorderColor(null);
    this.setBorderWidth(0);
  }

  toJSON() {
    const result = super.toJSON();
    return removeStaticAttributes(result);
  }
}

export class EntryView extends LabeledNodeView {}

export class EntryController extends LabeledNodeController {}

util.assign(shapes, {
  Entry,
  EntryView,
  EntryController,
});
